import { memo, useState, type ReactNode } from "react";
import { MousePointerClick } from "lucide-react";

export const ReflectionCard = memo(function ReflectionCard({
  children,
  ask,
  a,
  b,
  onPick,
  disabled = false,
  compactOnMobile = false,
}: {
  children?: ReactNode;

  ask?: string;
  a?: string;
  b?: string;
  onPick?: (index: number) => void;
  disabled?: boolean;
  compactOnMobile?: boolean;
}) {
  const labels = ask && a && b ? [a, b] : null;
  const gated = labels !== null;
  const [picked, setPicked] = useState<string | null>(null);

  if (labels && picked === null) {
    return (
      <aside
        className="my-8 rounded-2xl border border-dashed border-main bg-surface p-5"
        data-answer-ask
      >
        <div
          className={`flex items-start justify-between gap-3${
            compactOnMobile
              ? " max-sm:flex-col max-sm:items-start"
              : ""
          }`}
        >
          <p
            className={`min-w-0 font-noto max-sm:text-[0.9375rem] text-sm font-bold text-main${
              compactOnMobile ? " max-sm:order-2" : ""
            }`}
          >
            {ask}
          </p>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1 font-noto max-sm:text-[0.9375rem] text-[11px] font-bold text-[color:var(--color-text-on-primary)]${
              compactOnMobile ? " max-sm:order-1" : ""
            }`}
          >
            <MousePointerClick className="h-3 w-3" aria-hidden="true" />
            考えてみよう
          </span>
        </div>

        <p className="mt-1 font-noto max-sm:text-[0.9375rem] text-xs leading-6 text-sub">
          正解はありません。どちらを選んでも同じ答えが開きます。
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          {labels.map((label, index) => (
            <button
              key={label}
              type="button"
              disabled={disabled}
              onClick={() => {
                setPicked(label);
                onPick?.(index);
              }}
              className={`mb-[2px] min-h-11 flex-1 !cursor-pointer rounded-lg border border-main bg-surface px-4 py-2 text-left font-noto max-sm:text-[0.9375rem] text-sm font-bold text-main shadow-[0_2px_0_0_var(--color-border-main)] transition-colors hover:border-primary hover:text-primary active:translate-y-[2px] active:shadow-none motion-reduce:active:translate-y-0${
                compactOnMobile ? " max-sm:w-full" : ""
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <>
      {gated && (
        <p className="mt-8 font-noto max-sm:text-[0.9375rem] text-sm text-sub">
          あなたの答え <span className="font-bold text-main">{picked}</span>
        </p>
      )}
      <aside
        className={`flex items-start justify-end gap-3 ${gated ? "mt-2 mb-8" : "my-8"}`}
        data-answer-note
      >
        <div className="relative min-w-0 flex-1 rounded-2xl border border-main bg-surface px-5 py-4">
          <span
            aria-hidden="true"
            className="absolute right-[-7px] top-5 h-3.5 w-3.5 rotate-45 border-r border-t border-main bg-surface"
          />
          <div className="text-base leading-[1.9] text-main [&>p]:my-0 [&>p:not(:last-child)]:mb-2">
            {children}
          </div>
        </div>
        <img
          src="/mascot/fukurou-01.svg"
          alt=""
          aria-hidden="true"
          width={48}
          height={47}
          className={`mt-1 shrink-0${
            compactOnMobile ? " max-sm:hidden" : ""
          }`}
        />
      </aside>
    </>
  );
});
